import { TouchEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import usePrevious from '../hooks/usePrevious';
import Board from '../lib/board';
import { ANIMATION_COOLDOWN, ANIMATION_DURATION } from '../lib/constants';
import * as tutorial from '../lib/tutorial';
import { TUTORIAL_SCREENS, TutorialScreen } from '../lib/tutorial';
import {
  Bonus,
  CellValue,
  ColumnOf,
  DotValue,
  Empty,
  GameMode,
  SavableGameMode,
  ScoreData,
} from '../lib/types';
import { formatWithCommas, isCracked, isDotValue, isSolid, sleep } from '../lib/util';
import {
  BoardContainer,
  BonusText,
  Cell,
  CellScore,
  ColumnContainer,
  Combo,
  CrackedPiece,
  Drop,
  DropCell,
  Drops,
  GameContainer,
  GameContainerInner,
  GameHeader,
  GameMeta,
  Level,
  NumberPiece,
  PauseButton,
  Score,
  SolidPiece,
} from './game.styles';
import GameOverScreen from './gameOverScreen';
import Menu from './menu';
import Tutorial from './tutorial';

type GameProps = {
  board: Board;
  scores: ScoreData;
  restart: () => void;
  exit: () => void;
  updateScore: (mode: SavableGameMode, score: number) => void;
};

export default function Game({ board, scores, restart, exit, updateScore }: GameProps) {
  const [gameState, setGameState] = useState(() => board.getGameState());
  const [leveledUp, setLeveledUp] = useState(false);
  const [boardCleared, setBoardCleared] = useState(false);
  const [combo, setCombo] = useState(0);
  const [longestCombo, setLongestCombo] = useState(1);

  const isTutorial = board.mode === GameMode.Tutorial;
  const [tutorialScreen, setTutorialScreen] = useState<TutorialScreen | null>(
    () => isTutorial ? 1 : null
  );
  const advanceTutorial = useMemo(() => tutorialScreen ? () => {
    if (tutorialScreen >= TUTORIAL_SCREENS) {
      exit();
    } else {
      setTutorialScreen(tutorialScreen + 1 as TutorialScreen);
    }
  } : undefined, [exit, tutorialScreen]);

  const [hoveredColumn, setHoveredColumn] = useState(3);
  const [disabled, setDisabled] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const pause = useCallback(() => setShowMenu(true), []);
  const resume = useCallback(() => setShowMenu(false), []);

  const disabledForTutorial = useMemo(
    () => tutorialScreen ? !tutorial.requiredColumns[tutorialScreen] : false,
    [tutorialScreen]
  );
  const validTutorialColumns = useMemo(
    () => disabledForTutorial ? null : tutorial.requiredColumns[tutorialScreen!]!,
    [disabledForTutorial, tutorialScreen]
  );
  const highlightSolids = useMemo(
    () => tutorialScreen ? !!tutorial.highlightSolids[tutorialScreen] : false,
    [tutorialScreen]
  );

  const isDisabled = useMemo(() => {
    if (disabled || gameState.gameOver || leveledUp || boardCleared) {
      return true;
    }
    return disabledForTutorial;
  }, [boardCleared, disabled, disabledForTutorial, gameState.gameOver, leveledUp]);

  // manually manage a ref of the previous score data
  const prevScores = useRef(scores);
  // save score on game over
  const prevGameOver = usePrevious(gameState.gameOver);
  useEffect(() => {
    if (gameState.gameOver && !prevGameOver) {
      if (board.mode !== GameMode.Tutorial) {
        updateScore(board.mode, gameState.score);
      }
    }
  }, [board.mode, gameState.gameOver, gameState.score, prevGameOver, updateScore]);

  // handle board instance change when game is restarted
  const prevBoard = usePrevious(board);
  useEffect(() => {
    if (prevBoard && board !== prevBoard) {
      setGameState(board.getGameState());
      setHoveredColumn(3);
      setDisabled(false);
      setShowMenu(false);
      prevScores.current = scores;
    }
  }, [board, prevBoard, scores]);

  const prevLeveledUp = usePrevious(leveledUp);
  useEffect(() => {
    if (leveledUp && !prevLeveledUp) {
      window.setTimeout(() => {
        setLeveledUp(false);
      }, ANIMATION_DURATION * 1.5);
    }
  }, [leveledUp, prevLeveledUp]);

  const prevBoardCleared = usePrevious(boardCleared);
  useEffect(() => {
    if (boardCleared && !prevBoardCleared) {
      window.setTimeout(() => {
        setBoardCleared(false);
      }, ANIMATION_DURATION * 2);
    }
  }, [boardCleared, prevBoardCleared]);

  const drop = useCallback(async () => {
    if (disabledForTutorial) return;
    if (validTutorialColumns && !validTutorialColumns.has(hoveredColumn)) {
      return;
    }
    if (isDisabled || board.isColumnFull(hoveredColumn)) return;
    setDisabled(true);
    const it = board.drop(hoveredColumn);
    let result = it.next();
    let newLongestCombo = longestCombo;
    while (!result.done) {
      const newState = result.value;
      setGameState(newState);
      setCombo(newState.combo);
      if (newState.combo > newLongestCombo) {
        newLongestCombo = newState.combo;
      }
      if (newState.bonus === Bonus.LevelUp) {
        setLeveledUp(true);
      } else if (newState.bonus === Bonus.BoardClear) {
        setBoardCleared(true);
      }
      result = it.next();
      let sleepTime;
      if (result.done) {
        sleepTime = 150;
        setCombo(1);
      } else {
        sleepTime = ANIMATION_DURATION;
      }
      await sleep(sleepTime);
      const cooldownState = board.getCooldownState();
      setGameState(cooldownState);
      setCombo(cooldownState.combo);
      await sleep(ANIMATION_COOLDOWN);
    }
    if (newLongestCombo > longestCombo) {
      setLongestCombo(newLongestCombo);
    }
    setDisabled(false);
    if (advanceTutorial) {
      setHoveredColumn(3);
      advanceTutorial();
    }
  }, [
    advanceTutorial,
    board,
    disabledForTutorial,
    hoveredColumn,
    isDisabled,
    longestCombo,
    setLongestCombo,
    validTutorialColumns,
  ]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isDisabled && !disabledForTutorial) return;
      if (e.key === 'Escape') {
        setShowMenu((current) => !current);
      }
      if (disabledForTutorial || showMenu) return;
      switch (e.key.toLowerCase()) {
        case 'enter':
        case 'arrowdown':
        case ' ':
        case 's':
          drop().catch(console.error);
          return;
        case 'arrowleft':
        case 'a':
          if (hoveredColumn > 0) {
            setHoveredColumn(hoveredColumn - 1);
          }
          return;
        case 'arrowright':
        case 'd':
          if (hoveredColumn < 6) {
            setHoveredColumn(hoveredColumn + 1);
          }
          return;
        default:
          return;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [disabledForTutorial, drop, hoveredColumn, isDisabled, showMenu]);

  const drops = useMemo(
    () => new Array(gameState.dropsTotal)
      .fill(null)
      .map((_, index) => <Drop key={index} unused={index < gameState.dropsRemaining} />),
    [gameState.dropsRemaining, gameState.dropsTotal]
  );

  const onTouchMove = useCallback((e: TouchEvent) => {
    const el = document.elementFromPoint(e.touches[0].clientX, e.touches[0].clientY);
    if (el instanceof HTMLElement && el.dataset.column !== undefined) {
      setHoveredColumn(parseInt(el.dataset.column));
    }
  }, []);

  const renderCellScore = (value: DotValue, score: number) => {
    if (score === 0) return null;
    return <CellScore $value={value}>+{score}</CellScore>;
  };

  const renderCellContent = (value: CellValue, score: number) => {
    if (value === Empty) {
      return null
    }
    if (isCracked(value)) {
      return <CrackedPiece />;
    }
    if (isSolid(value)) {
      return <SolidPiece />;
    }
    return (
      <>
        <NumberPiece $value={value} score={score}>{value}</NumberPiece>
        {renderCellScore(value, score)}
      </>
    );
  };

  const renderCell = (value: CellValue, index: number, highlighted: boolean, score: number) => (
    <Cell key={index}
          $value={isDotValue(value) ? value : undefined}
          highlighted={highlighted}>
      {renderCellContent(value, score)}
    </Cell>
  );

  const renderColumn = (column: ColumnOf<CellValue>, index: number) => {
    const hovered = hoveredColumn === index;
    const columnHighlights = gameState.highlights[index];
    const columnScores = gameState.cellScores[index];
    const activelyHovered = hovered && !isDisabled;
    const allowedForTutorial = !validTutorialColumns || validTutorialColumns.has(hoveredColumn);
    const requiredForTutorial = !isDisabled &&
    validTutorialColumns ? validTutorialColumns.has(index) : false;
    return (
      <ColumnContainer
        key={index}
        data-column={index}
        hovered={activelyHovered}
        $allowed={allowedForTutorial}
        $required={requiredForTutorial}
        $highlightSolids={highlightSolids}
        onPointerEnter={() => {
          if (disabledForTutorial) return;
          setHoveredColumn(index);
        }}
        onPointerUp={drop}
      >
        <DropCell key='drop'>
          {(activelyHovered || (disabledForTutorial && hovered)) &&
            renderCellContent(gameState.currentDrop, 0)
          }
        </DropCell>
        {column.map((value, i) => renderCell(
          value,
          i,
          columnHighlights[i],
          columnScores[i],
        ))}
      </ColumnContainer>
    );
  };

  return (
    <GameContainer onTouchMove={onTouchMove} onTouchStart={onTouchMove}>
      <GameContainerInner>
        {tutorialScreen && (
          <Tutorial screen={tutorialScreen}
                    visible={!disabled}
                    advance={disabledForTutorial ? advanceTutorial : undefined}/>
        )}
        {showMenu && (
          <Menu mode={board.mode} resume={resume} restart={restart} exit={exit}/>
        )}
        {gameState.gameOver && (
          <GameOverScreen mode={board.mode}
                          score={gameState.score}
                          level={gameState.level}
                          longestChain={longestCombo}
                          scores={scores}
                          prevScores={prevScores.current}
                          restart={restart}
                          exit={exit}
          />
        )}
        {combo > 1 && (
          <Combo tutorial={isTutorial}>{`Chain × ${combo}`}</Combo>
        )}
        <GameMeta tutorial={isTutorial}>
          <tbody>
          <tr>
            <th>Mode:</th>
            <td>{GameMode[board.mode]}</td>
          </tr>
          <tr>
            <th>Best:</th>
            <td>
              {board.mode === GameMode.Tutorial
                ? ''
                : formatWithCommas(scores[board.mode].best)}
            </td>
          </tr>
          </tbody>
        </GameMeta>
        <GameHeader>
          <Score>{formatWithCommas(gameState.score)}</Score>
          <PauseButton
            title="Pause"
            disabled={showMenu || (isDisabled && !disabledForTutorial)}
            onKeyDown={(e) => {
              e.stopPropagation();
              e.currentTarget.blur();
              pause();
            }}
            onClick={pause}/>
        </GameHeader>
        {!gameState.gameOver && (
          <>
            <BonusText tutorial={isTutorial}
                       visible={leveledUp}
                       shadePieces={isTutorial && disabledForTutorial}>
              {`Level bonus +${formatWithCommas(board.levelUpBonus)}`}
            </BonusText>
            <BonusText tutorial={isTutorial}
                       visible={boardCleared && !leveledUp}
                       shadePieces={isTutorial && disabledForTutorial}>
              {`Clear +${formatWithCommas(board.clearBonus)}`}
            </BonusText>
          </>
        )}
        <BoardContainer $disabled={isDisabled && !disabledForTutorial}
                        tutorial={isTutorial}
                        shadePieces={isTutorial && !disabled}>
          {gameState.columns.map(renderColumn)}
        </BoardContainer>
        {!isTutorial && (
          <>
            <Drops>{drops}</Drops>
            <Level>Level {gameState.level}</Level>
          </>
        )}
      </GameContainerInner>
    </GameContainer>
  );
}
